import {LayoutEditDialogComponent} from './layout-edit-dialog/layout-edit-dialog.component';
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {Injectable} from '@angular/core';
import {Project} from '@docma-mm-core/project/project';
import {User} from '@docma-mm-core/user/user';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {ConnectivityService} from '@mydocma/core';
import {Layout} from '@docma-mm-core/layout/layout';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {LayoutApiService} from '@docma-mm-core/layout/layout-api.service';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {ActionPermissionEnum} from '@docma-mm-core/permission/action-permission.enum';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {LayoutDuplicateDialogComponent} from './layout-duplicate-dialog/layout-duplicate-dialog.component';
import {LayoutStateModel} from '../layout-state.model';
import {MmLayoutStateService} from '../layout-state.service';
import {ColDefsService} from '../../../mm-faults/mm-faults-ag-grid/col-defs/col-defs.service';
import {LayoutField} from '@docma-mm-core/layout/layout-field';
import {getDuplicateName, isIdsEquals} from '@docma-mm-core/utils/utils';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import { RowDefsService } from '../../../mm-faults/mm-faults-ag-grid/row-defs/row-defs.service';

@Injectable()
export class LayoutEditService
{
  private dialogRef: MatDialogRef<any>;

  layoutAddedSubject = new Subject<{Layout: Layout, Layouts: Layout[]}>();
  layoutChangedSubject = new Subject<{Layout: Layout, Layouts: Layout[]}>();
  layoutDeletedSubject = new Subject<{Layout: Layout, Layouts: Layout[]}>();


  layoutAdded$ = this.layoutAddedSubject.asObservable();
  layoutChanged$ = this.layoutChangedSubject.asObservable();
  layoutDeleted$ = this.layoutDeletedSubject.asObservable();

  isOnline: boolean = false;

  permissionEditGlobalLayout$ = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditGlobalLayout]);
  permissionEditProjectLayout$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditProjectLayout]);

  layoutStatesSubject = new BehaviorSubject<{[suid: string]: boolean}>({});
  public layoutStates$ = this.layoutStatesSubject.asObservable();
  constructor(public dialog: MatDialog,
              private readonly connectivityService: ConnectivityService,
              private readonly layoutApiService: LayoutApiService,
              private readonly notificationService: NotificationService,
              private translateService: TranslateService,
              private readonly dialogService: PromptDialogService,
              public readonly permissionService: MmProjectPermissionsService,
              public readonly layoutStateService: MmLayoutStateService,
              private readonly colDefsService: ColDefsService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly rowDefsService: RowDefsService) {

    this.connectivityService.isOffline
      .subscribe(isOffline => {
        // console.log(`IS ONLINE: ${!isOffline}`);
        this.isOnline = !isOffline;
      });
  }

  isAppOnline(showNotification: boolean) {
    if (showNotification && !this.isOnline) {
      this.notificationService.warning('Function not available - app is in offline Mode!');
    }
    return this.isOnline;
  }

  addLayout(scope: EntityScopeEnum, project: Project, user: User) {
    if (!this.isAppOnline(true)) {
      return;
    }

    this.dialogRef = this.dialog.open(LayoutEditDialogComponent, {
      data: {
        layoutId: null,
        scope: scope,
        project: project,
        user: user,
        service: this,
        isNew: true
      },
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-layout-edit-dialog'],
      autoFocus: false,
      disableClose: true });

    this.dialogRef.afterClosed().subscribe(async (result: {Layout: Layout, IsNew: boolean, ForceReload: boolean}) => {
      if (result?.Layout != null) {
        var layouts = await this.projectLoaderService.loadLayoutsByAddedOrUpdated(project, result.Layout, true).toPromise();
        this.layoutAddedSubject.next({Layout: result.Layout, Layouts: layouts});      // update layout-left-pane component
        this.notificationService.success(this.translateService.instant('MM.layout.layoutSaved'));
      }
    });
  }

  editLayout(layoutId: number, scope: EntityScopeEnum, project: Project, user: User) {
    if (!this.isAppOnline(true)) {
      return;
    }

    this.dialogRef = this.dialog.open(LayoutEditDialogComponent, {
      data: {
        layoutId: layoutId,
        scope: scope,
        project: project,
        user: user,
        service: this,
        isNew: false
      },
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-layout-edit-dialog'],
      autoFocus: false,
      disableClose: true });

    this.dialogRef.afterClosed().subscribe(async (result: {Layout: Layout, IsNew: boolean, ForceReload: boolean}) => {
      if (result?.Layout != null) {
        var layouts = await this.projectLoaderService.loadLayoutsByAddedOrUpdated(project, result.Layout, true).toPromise();
        await this.layoutStateService.updateLayout(result.Layout);                          // update current layout state
        this.layoutChangedSubject.next({Layout: result.Layout, Layouts: layouts});    // update layout-left-pane component
        await this.updateAgGrid(result.Layout, project, result.ForceReload);              // update ag-grid
        this.notificationService.success(this.translateService.instant('MM.layout.layoutUpdated'));
      }
    });
  }

  async deleteLayout(layout: Layout, project: Project) {
    if (!this.isAppOnline(true)) {
      return;
    }

    let title =  await this.translateService.get('MM.layout.deleteMessage.title', {value: layout.Name}).toPromise();
    let content: string =  await this.translateService.get('MM.layout.deleteMessage.content', {value: layout.Name}).toPromise();

    const continueDelete = (await this.dialogService.showDeleteConfirmationDialog({title, content}))?.value ?? false;

    try {
      if (continueDelete) {
        await this.layoutApiService.deleteLayout(layout.Id).toPromise();
        var layouts = await this.projectLoaderService.loadLayoutsByDeleted(project, layout.Id, true).toPromise();
        this.layoutDeletedSubject.next({Layout: layout, Layouts: layouts});           // update layout-left-pane component
        this.notificationService.success(this.translateService.instant('MM.layout.layoutDeleted'));
      }
    }
    catch (err) {
      if (err?.error?.Code === "8000004") {
        this.notificationService.error(err.error.Message);
      }
      else {
        this.notificationService.error(this.translateService.instant('MM.layout.errorDeletingLayout'));
      }
    }
  }

  async duplicateLayout(layout: Layout, layouts: Layout[], project: Project, user: User) {
    if (!this.isAppOnline(true)) {
      return;
    }

    const layoutNames: { [scope: number]: string[] } = {};
    const groupedLayouts = this.groupedLayouts(layouts)

    for (const scope of Object.keys(groupedLayouts)) {
      layoutNames[scope] = groupedLayouts[scope].map(x => x.Name);
    }

    const nameSuffix = getDuplicateName(layout.Name, layoutNames[+layout.Scope], this.translateService);

    this.dialogRef = this.dialog.open(LayoutDuplicateDialogComponent, {
      data: {
        layoutId: layout.Id,
        project: project,
        user: user,
        service: this,
        nameSuffix: nameSuffix,
        layoutNames: layoutNames
      },
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-layout-duplicate-dialog'],
      autoFocus: false,
      disableClose: true });

    this.dialogRef.afterClosed().subscribe(async (result: {Layout: Layout, IsNew: boolean}) => {
      if (result?.Layout != null) {

        // MMW-2047: Duplicated layouts should always be user favorite
        result.Layout.IsFavoriteUser = true;

        var layouts = await this.projectLoaderService.loadLayoutsByAddedOrUpdated(project, result.Layout, true).toPromise();
        this.layoutAddedSubject.next({Layout: result.Layout, Layouts: layouts});      // update layout-left-pane component
        this.notificationService.success(this.translateService.instant('MM.layout.layoutDuplicated'));
      }
    });
  }

  async setFavorite(layout: Layout, project: Project, value: boolean, updateField: string) {
    if (!this.isAppOnline(true)) {
      return;
    }

    try {
      const patch = { path: `/${updateField}`, op: 'replace', value: value};

      const updatedLayout = await this.layoutApiService.patchLayout(layout.Id, [patch]).toPromise();
      var layouts = await this.projectLoaderService.loadLayoutsByAddedOrUpdated(project, updatedLayout, true).toPromise();
      await this.layoutStateService.updateLayout(updatedLayout);                      // update current layout state
      this.layoutChangedSubject.next({Layout: updatedLayout, Layouts: layouts});      // update layout-left-pane component
      this.notificationService.success(this.translateService.instant('MM.layout.layoutUpdated'));
    }
    catch (err) {
      this.notificationService.error(this.translateService.instant('MM.layout.errorUpdatingLayout'))
    }
  }

  async saveForAll(layout: Layout, project: Project) {
    if (!this.isAppOnline(true)) {
      return;
    }

    const fullLayout = await firstValueFrom(this.layoutApiService.getFullLayout(layout.Id));

    // get current states
    const layoutState = await this.layoutStateService.getLayoutStateOfLayout(layout);
    let columnOrder: any[] = await this.layoutStateService.getLayoutColumnOrderOfLayout(layout);

    const patches: any[] = [];
    let i = 0;
    let iMax = 0;
    const normalizedColumnOrder: {[idx: number]: number} = {}
    const indexedFields: {[idx: number]: LayoutField} = {}

    // normalize column order based on 0
    columnOrder?.sort((a, b) => a.Index - b.Index).forEach(c => {
      normalizedColumnOrder[c.Field.Id] = i;
      i++;
    });

    // set new SortId for all layout fields
    i = 0;
    fullLayout.LayoutFields.sort((a, b) => a.SortId - b.SortId)
      .forEach(lf => {
        if (!normalizedColumnOrder.hasOwnProperty(lf.Field.Id)) {
          /* field not in columnOrder */
          indexedFields[iMax] = lf;
          i++;
          iMax++;
        }
        else {
          indexedFields[(normalizedColumnOrder[lf.Field.Id] + i)] = lf;
          iMax++;
        }
      });

    // for all fields with new SortId add patch
    for (let i of Object.keys(indexedFields)) {
      const found = fullLayout.LayoutFields.find(x => x.Field.Id === indexedFields[i].Field.Id);
      if (found && found.SortId !== +i) {
        patches.push({ path: `/LayoutFields/${indexedFields[i].Id}/SortId`, op: 'replace', value: +i});
      }
    }

    // add patch for all state
    if (layoutState?.Fields) {
      layoutState.Fields.forEach(ls => {
        const layoutField = fullLayout.LayoutFields.find(x => x.Field.Id === ls.Field.Id);

        if (ls.Width != null) {
          patches.push({path: `/LayoutFields/${layoutField.Id}/Width`, op: 'replace', value: (ls.Width * 10)});
        }

        if (ls.Sorting != null && ls.IsDescending != null) {
          patches.push({path: `/LayoutFields/${layoutField.Id}/Sorting`, op: 'replace', value: ls.Sorting});
          patches.push({path: `/LayoutFields/${layoutField.Id}/IsDescending`, op: 'replace', value: ls.IsDescending});
        }

        if (ls.Grouping != null) {
          patches.push({path: `/LayoutFields/${layoutField.Id}/Grouping`, op: 'replace', value: ls.Grouping})
        }
      });

      var test = fullLayout.LayoutFields.filter(lf => layoutState.Fields.findIndex(uf => uf.Field.Id === lf.Field.Id) === -1 && (lf.Sorting !== 0 || lf.IsDescending !== false));


      test.forEach(layoutField => {
          patches.push({path: `/LayoutFields/${layoutField.Id}/Sorting`, op: 'replace', value: 0});
          patches.push({path: `/LayoutFields/${layoutField.Id}/IsDescending`, op: 'replace', value: false});
        });
    }

    try {
      const updatedLayout = await this.layoutApiService.patchLayout(layout.Id, patches).toPromise();
      var layouts = await this.projectLoaderService.loadLayoutsByAddedOrUpdated(project, updatedLayout, true).toPromise();
      this.layoutChangedSubject.next({Layout: updatedLayout, Layouts: layouts});                // update layout-left-pane component
      this.notificationService.success(this.translateService.instant('MM.layout.layoutUpdated'));

      await this.layoutStateService.deleteLayoutStateOfLayout(layout);    // remove from index-db
      await this.layoutStateService.updateLayout(layout);                 // update current layout state
      // await this.revert(updatedLayout, project, true);
    }
    catch (err) {
      this.notificationService.error(this.translateService.instant('MM.layout.errorUpdatingLayout'))
    }

  }

  async revert(layout: Layout, project: Project, forceReload: boolean = false) {
    await this.layoutStateService.deleteLayoutStateOfLayout(layout);    // remove from index-db

    await this.layoutStateService.updateLayout(layout);                 // update current layout state
    await this.updateAgGrid(layout, project, forceReload);          // update ag-grid
  }

  async updateAgGrid(layout: Layout, project: Project, forceReload: boolean) {
    const currentState = await firstValueFrom(this.layoutStateService.currentLayoutState$);
    const isCurrentLayout = (currentState.state.project  && layout &&  isIdsEquals(currentState.state.layout, layout));

    const newState = new LayoutStateModel();
    newState.project = project;
    await this.layoutStateService.getLayoutStateModel(newState, layout, true, isCurrentLayout);

    await this.updateLayoutState(layout);

    if (isCurrentLayout) {
      if (forceReload) {
        await this.layoutStateService.setLayout(layout, true);
        // apply to ag-grid
        this.colDefsService.applyLayoutState(newState);
        this.rowDefsService.applyLineHeightToGrid()
      }
      else {
        // apply to ag-grid
        this.colDefsService.applyLayoutState(newState);
        this.rowDefsService.applyLineHeightToGrid()
      }
    }
  }

  // get current user layout states (layout user options)
  async getLayoutStates(layouts: Layout[]) {
    const layoutStates: {[suid: string]: boolean} = {};

    await Promise.all(
      layouts.map(async layout => {
        const layoutState = await this.layoutStateService.getLayoutStateOfLayout(layout);
        const columnOrder = await this.layoutStateService.getLayoutColumnOrderOfLayout(layout);
        if (layoutState || columnOrder) {
          layoutStates[layout.SUid] = true;
        }
        else {
          layoutStates[layout.SUid] = false;
        }
      })
    );
    return layoutStates;
  }

  // update has user layout states (layout user options)
  async updateLayoutState(layout: Layout) {
    const layoutStates = this.layoutStatesSubject.value;

    const layoutState = await this.layoutStateService.getLayoutStateOfLayout(layout);
    const columnOrder = await this.layoutStateService.getLayoutColumnOrderOfLayout(layout);
    if (layoutState || columnOrder) {
      layoutStates[layout.SUid] = true;
    }
    else {
      layoutStates[layout.SUid] = false;
    }

    this.layoutStatesSubject.next(layoutStates);
  }

  groupedLayouts(layouts: Array<Layout>) : { [scope: number]: Layout[] } {
    return layouts
      .reduce((acc: { [scope: number]: Layout[] }, curr) => {
        const scope: number = +curr.Scope;
        if (acc[scope] == null) {
          acc[scope] = [];
        }
        acc[scope].push(curr);
        return acc;
      }, {})
  }

}
